import { branding } from '@/config/branding';

/**
 * Truncates text to at most `maxLength` characters (no ellipsis appended).
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum number of characters to keep
 * @returns {string} the original text, or its first `maxLength` characters if longer
 */
export function getMaxLengthChars(text, maxLength) {
  return text.length > maxLength ? text.slice(0, maxLength) : text;
}

/**
 * Formats a timestamp as a short `M/D` date string.
 * @param {number|string|Date} timestamp - Timestamp/date to format
 * @returns {string} date formatted as `month/day`
 */
export function formatDateStringFromTimestamp(timestamp) {
  const dt = new Date(timestamp);
  let mm = dt.getMonth() + 1; // Months start at 0
  let dd = dt.getDate();
  return `${mm}/${dd}`;
}

/**
 * Truncates text to `maxLength` characters, appending `...` when truncated.
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum number of characters before truncation
 * @returns {string} the original text, or the truncated text with a trailing ellipsis
 */
export function trimText(text, maxLength) {
  let sliced = text.slice(0, maxLength);
  if (sliced.length < text.length) {
    return (sliced += '...');
  } else {
    return text;
  }
}

// SECURITY: Safe text sanitization to prevent XSS attacks
/**
 * Strips formatting tags (p/div/span/br) and any remaining HTML, then
 * escapes HTML entities, producing plain text safe for HTML display.
 * Newlines are re-encoded as `<br>` so line breaks are preserved visually.
 * @param {string} text - Raw text/HTML to sanitize
 * @returns {string} escaped, tag-stripped text with newlines as `<br>`
 */
export function sanitizeText(text) {
  if (!text) return '';
  
  // Convert to string first
  let cleanText = String(text);
  
  // FIXED: Strip common formatting tags that should not be visible
  cleanText = cleanText
    .replace(/<\/?p[^>]*>/gi, '') // Remove <p> and </p> tags
    .replace(/<\/?div[^>]*>/gi, '') // Remove <div> and </div> tags
    .replace(/<\/?span[^>]*>/gi, '') // Remove <span> and </span> tags
    .replace(/<br\s*\/?>/gi, '\n') // Convert <br> tags to newlines temporarily
    .replace(/<\/?[^>]+(>|$)/g, ''); // Remove any remaining HTML tags
  
  // Escape remaining HTML entities for safety
  cleanText = cleanText
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
  
  // Convert newlines to <br> tags for safe HTML display
  return cleanText.replace(/\n/g, '<br>');
}

// SECURITY: Safe HTML rendering with link detection
/**
 * Sanitizes rich text (preserving allowed formatting tags via `sanitizeRichText`)
 * and then wraps any bare URLs in the result as clickable links.
 * @param {string} text - Raw rich text/HTML to sanitize and link-wrap
 * @returns {string} sanitized HTML with URLs wrapped in `<a>` tags
 */
export function sanitizeTextWithLinks(text) {
  if (!text) return '';

  // For rich text content, use the more permissive sanitization
  const sanitized = sanitizeRichText(text);

  // Then apply safe link wrapping on the sanitized text
  return wrapLinksInText(sanitized);
}

// SECURITY: Sanitize rich text while preserving formatting tags
/**
 * Sanitizes HTML while preserving a whitelist of formatting tags (p, br,
 * strong, em, lists, headings, links, etc.). Allowed tags are temporarily
 * replaced with placeholders, all remaining tags are stripped, entities in
 * the remaining text are escaped, then the allowed tags are restored.
 * @param {string} text - Raw HTML to sanitize
 * @returns {string} HTML with only the allowed tags preserved and everything else escaped/stripped
 */
export function sanitizeRichText(text) {
  if (!text) return '';
  
  // Convert to string first
  let cleanText = String(text);
  
  // Define allowed HTML tags for rich text formatting
  const allowedTags = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 
    'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a'
  ];
  
  // Create regex to match allowed tags
  const allowedTagsRegex = new RegExp(`<\\/?(?:${allowedTags.join('|')})(?:\\s[^>]*)?>`, 'gi');
  
  // First extract all allowed tags
  const allowedTagMatches = cleanText.match(allowedTagsRegex) || [];
  
  // Create placeholders for allowed tags
  const tagPlaceholders = {};
  allowedTagMatches.forEach((tag, index) => {
    const placeholder = `__ALLOWED_TAG_${index}__`;
    tagPlaceholders[placeholder] = tag;
    cleanText = cleanText.replace(tag, placeholder);
  });
  
  // Remove all remaining HTML tags (potentially dangerous ones)
  cleanText = cleanText.replace(/<\/?[^>]+(>|$)/g, '');
  
  // Escape any remaining HTML entities for safety
  cleanText = cleanText
    .replace(/&(?!amp;|lt;|gt;|quot;|#x27;|#x2F;)/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  
  // Restore allowed tags
  Object.keys(tagPlaceholders).forEach(placeholder => {
    cleanText = cleanText.replace(placeholder, tagPlaceholders[placeholder]);
  });
  
  return cleanText;
}

/**
 * Produces a shortened, readable display form of a URL (strips `www.`,
 * drops the root `/` path, truncates with an ellipsis if still too long).
 * Falls back to naive truncation if the URL fails to parse.
 * @param {string} url - URL to shorten for display
 * @param {number} [maxLen=150] - Maximum display length before truncating
 * @returns {string} shortened display string for the URL
 */
function shortenUrlDisplay(url, maxLen = 150) {
  try {
    const { protocol, hostname, pathname, search } = new URL(url);
    const host = hostname.replace(/^www\./, '');
    const path = pathname && pathname !== '/' ? pathname : '';
    const display = protocol + '//' + host + path + search;
    return display.length > maxLen ? display.slice(0, maxLen - 1) + '…' : display;
  } catch {
    return url.length > maxLen ? url.slice(0, maxLen - 1) + '…' : url;
  }
}

/**
 * Wraps bare URLs in text with clickable `<a>` tags (shortening their display
 * text), while leaving existing `<a>` tags alone except shortening the
 * display text of any that directly wrap a raw URL — this avoids double
 * -wrapping links already present in post HTML. Also puts consecutive,
 * space-separated link tags each on their own line.
 * @param {string} text - Text/HTML that may contain bare URLs and/or existing `<a>` tags
 * @returns {string} text with URLs wrapped as links
 */
export function wrapLinksInText(text) {
  if (!text) return text;

  // Match existing <a>…</a> blocks (shorten display if inner text is a raw URL) or bare URLs (wrap them).
  // This prevents double-wrapping when post HTML already contains anchor tags.
  let result = text.replace(/(<a\b[^>]*>)([\s\S]*?)(<\/a>)|(https?:\/\/[^\s<>"']+)/gi, (match, open, inner, close, url) => {
    if (open) {
      const innerTrimmed = (inner ?? '').trim();
      if (/^https?:\/\//i.test(innerTrimmed)) {
        return open + shortenUrlDisplay(innerTrimmed) + close;
      }
      return match;
    }
    const display = shortenUrlDisplay(url);
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" title="${url}">${display}</a>`;
  });
  // Put each URL on its own line when multiple URLs are space-separated in the same paragraph
  result = result.replace(/(<\/a>)\s+(<a\b[^>]*>https?:\/\/)/g, '$1<br>$2');
  return result;
}

const FORBIDDEN_USERNAME_KEYWORDS = ['thechangenetwork', 'changenetwork', 'admin', 'staff', 'observer', 'superadmin', 'member'];

// Collapses runs of a repeated character down to one (e.g. "theeeechangeee" -> "thechange"),
// so padding out a reserved word with repeated letters no longer dodges the substring check.
// Applied to the keyword list too, so a keyword with a legitimate double letter (e.g. "staff")
// still matches a plainly-typed username after both sides go through the same normalization.
/**
 * Collapses runs of a repeated character down to a single instance.
 * @param {string} str - String to collapse
 * @returns {string} string with consecutive duplicate characters reduced to one
 */
function collapseRepeatedChars(str) {
  return str.replace(/(.)\1+/g, '$1');
}

const COLLAPSED_FORBIDDEN_USERNAME_KEYWORDS = FORBIDDEN_USERNAME_KEYWORDS.map(collapseRepeatedChars);

/**
 * Checks whether a username contains a forbidden reserved keyword, after
 * normalizing away underscores and collapsing repeated letters so obfuscated
 * variants (e.g. `ad_min`, `theeeechangeeee`) are also caught. Only enforced
 * under the `dougjoneslight` brand.
 * @param {string} username - Username to check
 * @returns {boolean} true if the username matches a forbidden keyword under the current brand
 */
export function containsForbiddenUsernameKeyword(username) {
  if (!username) return false;
  // Restricted keywords are a dougjoneslight-specific rule, not a global one.
  if (branding.themePreset !== 'dougjoneslight') return false;
  // Usernames only allow letters, numbers, and underscores, so underscores and
  // repeated letters are the only characters someone could sprinkle/stretch in to
  // dodge the check (e.g. "ad_min", "theeeeechangeeeeenetwork"). Stripping
  // underscores and collapsing repeats before matching catches every such variant
  // without having to enumerate each one.
  const normalized = collapseRepeatedChars(username.toLowerCase().replace(/_/g, ''));
  return COLLAPSED_FORBIDDEN_USERNAME_KEYWORDS.some((keyword) => normalized.includes(keyword));
}

// Alias kept for other call sites still importing the old name.
/** Alias of {@link containsForbiddenUsernameKeyword}, kept for older call sites. */
export const containsForbiddenUsername = containsForbiddenUsernameKeyword;

/**
 * Returns true when the OG description is redundant — i.e. it contains the same
 * core text as the title (common on Instagram/TikTok where title and description
 * both repeat the post caption). Uses the first 40 chars of the title as a
 * fingerprint; avoids hiding genuinely different descriptions on normal websites.
 * @param {string} title - OG title text
 * @param {string} description - OG description text
 * @returns {boolean} true if the description appears to just repeat the title
 */
export function isOgDescriptionRedundant(title, description) {
  if (!title || !description) return false;
  const snippet = title.trim().toLowerCase().slice(0, 40);
  return snippet.length > 10 && description.toLowerCase().includes(snippet);
}
